/**
 * Settings Layout
 * Shared shell for every /settings/* page, rendered inside AppLayout.
 * The index route (SettingsIndexPage) shows SETTINGS_ITEMS as cards; add new
 * settings pages there (and a route under the settings <Route> in App.tsx).
 */

import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User } from '../api/types';
import { canManageMfaPolicy, canUseMfa, canManageChecklistTemplates, canManageCompanySettings, canManageDispatch, canManageLeadSources, canManageNotifications, canManageOnlinePayments, canManagePortalSettings, canManageUsers, canManageWebFormIntake, canManageGeocoding, canSendNotifications, canViewAuditLog, canExportData, canViewStaffNotifications, canManageSetup, canManageStarterTemplates, canViewRecurringPresets, canViewSurveys, canManagePortalRollout, canManageBundles } from '../utils/permissions';
import { SETUP_PATH, cameFromSetup } from '../utils/setupWizard';

export interface SettingsItem {
  to: string;
  /** Section heading the card is listed under on the index page; groups appear in first-use order. */
  group: string;
  label: string;
  description: string;
  /** Hide the card from users who cannot use the page (UX only; the page and backend enforce access). */
  isVisible?: (user: User | null) => boolean;
}

export const SETTINGS_ITEMS: SettingsItem[] = [
  {
    group: 'Company & Team',
    to: '/settings/company',
    label: 'Company',
    description: 'Company name, address and contact details, payment terms, late fees, scheduling and travel estimates.',
  },
  {
    group: 'Company & Team',
    to: '/settings/users',
    label: 'Users',
    description: 'Staff accounts, roles, and access.',
    isVisible: canManageUsers,
  },
  {
    group: 'Company & Team',
    to: '/account/security',
    label: 'My Two-Step Verification',
    description: 'Sign in with a code from an authenticator app; recovery codes.',
    isVisible: canUseMfa,
  },
  {
    group: 'Company & Team',
    to: '/settings/my-alerts',
    label: 'My Alerts',
    description: 'Which staff alerts (new requests, customer replies, payments, finished jobs) you get in the notification bell.',
    isVisible: canViewStaffNotifications,
  },
  {
    group: 'Company & Team',
    to: '/settings/security',
    label: 'Two-Step Verification Policy',
    description: 'Which staff roles must use an authenticator app to sign in.',
    isVisible: canManageMfaPolicy,
  },
  {
    group: 'Company & Team',
    to: '/settings/technician-schedules',
    label: 'Technician Schedules',
    description: 'Per-technician working hours and time off for the dispatch board.',
    isVisible: canManageDispatch,
  },
  {
    group: 'Company & Team',
    to: '/settings/routes',
    label: 'Routes',
    description: 'Named service routes, technicians’ usual routes, and date-specific coverage.',
    isVisible: canManageDispatch,
  },
  {
    group: 'Company & Team',
    to: '/settings/geocoding',
    label: 'Geocoding',
    description: 'How many service addresses are on the map, the company location, and retrying failed lookups.',
    isVisible: canManageGeocoding,
  },
  {
    group: 'Company & Team',
    to: '/settings/lead-sources',
    label: 'Lead Sources',
    description: 'Where customers come from, with inherited UTM defaults.',
    isVisible: canManageLeadSources,
  },
  {
    group: 'Company & Team',
    to: '/settings/audit-log',
    label: 'Audit Log',
    description: 'Who changed users, settings, and financial records, and when.',
    isVisible: canViewAuditLog,
  },
  {
    group: 'Company & Team',
    to: '/settings/import-export',
    label: 'Import / Export',
    description: 'Bring in customers, service addresses and items from CSV or QuickBooks; download your data as CSV.',
    isVisible: canExportData,
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/items',
    label: 'Items',
    description: 'Services, labor, products, and fees with prices, costs, and GL accounts.',
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/price-charts',
    label: 'Price Charts',
    description: 'Size bands that price services from a property’s size.',
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/checklist-templates',
    label: 'Checklist Templates',
    description: 'Reusable job checklists; link one to an item to copy its steps onto jobs.',
    isVisible: canManageChecklistTemplates,
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/starter-packs',
    label: 'Industry Starter Packs',
    description: 'Add ready-made items, price charts, checklists and lead sources for lawn care, pest control, HVAC, cleaning or pool service.',
    isVisible: canManageStarterTemplates,
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/service-bundles',
    label: 'Service Bundles',
    description: 'Programs sold again and again, such as spring fertilizer then weed control 45 days later; renewal alerts and who receives them.',
    isVisible: canManageBundles,
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/recurring-presets',
    label: 'Recurring Presets',
    description: 'Reusable repeat patterns and billing terms to start a recurring schedule from.',
    isVisible: canViewRecurringPresets,
  },
  {
    group: 'Catalog & Pricing',
    to: '/settings/discounts',
    label: 'Discounts',
    description: 'Reusable dollar and percentage discounts.',
  },
  {
    group: 'Accounting & Payments',
    to: '/settings/chart-of-accounts',
    label: 'Chart of Accounts',
    description: 'The general ledger account structure.',
  },
  {
    group: 'Accounting & Payments',
    to: '/settings/tax-rates',
    label: 'Tax Rates',
    description: 'Sales tax rates and their payable GL accounts.',
  },
  {
    group: 'Accounting & Payments',
    to: '/settings/payments',
    label: 'Payments',
    description: 'Card processor connection, online payments on/off, and autopay retry rules.',
    isVisible: canManageOnlinePayments,
  },
  {
    group: 'Accounting & Payments',
    to: '/settings/dunning',
    label: 'Dunning',
    description: 'Automatic overdue reminders: on/off, schedule of steps, and timing.',
    isVisible: canManageCompanySettings,
  },
  {
    group: 'Communications',
    to: '/settings/notifications',
    label: 'Notifications',
    description: 'Email sending connection, the on/off switch, and the wording of each message.',
    isVisible: canManageNotifications,
  },
  {
    group: 'Communications',
    to: '/settings/customer-portal',
    label: 'Customer Portal',
    description: 'Online accounts for customers: on/off, invoice code lifetime, logins per customer, and reschedule rules.',
    isVisible: canManagePortalSettings,
  },
  {
    group: 'Communications',
    to: '/settings/portal-rollout',
    label: 'Portal Rollout',
    description: 'Invite many customers to the customer portal in batches after a migration, see who has activated, and follow up with the rest.',
    isVisible: canManagePortalRollout,
  },
  {
    group: 'Communications',
    to: '/settings/surveys',
    label: 'Customer Surveys',
    description: 'Ask customers to rate a finished visit: on/off, how long to wait, what counts as a low score, and who is asked.',
    isVisible: canViewSurveys,
  },
  {
    group: 'Communications',
    to: '/settings/web-form',
    label: 'Web Form Intake',
    description: 'A service request form for your website: on/off, the form key, a copy-paste snippet, and regenerating the key.',
    isVisible: canManageWebFormIntake,
  },
  {
    group: 'Communications',
    to: '/settings/notifications/history',
    label: 'Message History',
    description: 'Every email queued or sent, with retry and cancel.',
    isVisible: canSendNotifications,
  },
];

export function SettingsLayout() {
  const { user } = useAuth();
  const location = useLocation();
  // Opened from the setup guide (tracker item 75): a way back to it
  const fromSetup = canManageSetup(user) && cameFromSetup(location.state);
  return (
    <>
      {fromSetup && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Link to={SETUP_PATH} className="inline-flex items-center min-h-[44px] sm:min-h-0 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline">
            Back to the setup guide
          </Link>
        </div>
      )}
      <Outlet />
    </>
  );
}
